import { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, EyeIconSmall, ThreeDotsIcon } from '../../../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/popover/index';
import { DividendPayoutRow } from '../../../../../hooks/dividend/use-dividend-payout-rows.hook';
import { DividendHooks, useModalState, usePagination } from '../../../../../hooks/index';
import { StringKey } from '../../../../../lang/index';
import { selectedCompanyStorage } from '../../../../../storage/company/index';
import { DividendTimelineEvent } from '../../../../../types/events.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../../../utils/company-storage-url.util';
import { DividendDetailsSheet } from '../../../../dividends/components/dividend-details-sheet';
import { DividendStakeholderDetailsSheet } from '../../../../dividends/components/dividend-stakeholder-details-sheet';
import {
  formatDividendAmount,
  formatDividendPerShare,
} from '../../../../dividends/utils/format-dividend-amount.util';
import { formatDividendPeriod } from '../../../../dividends/utils/format-dividend-period.util';
import { EventTransactionFilesCell } from '../../../components/event-transaction-files-cell';
import { FilesModal } from '../../../modals/index';
import { Tag, TagVariant } from '../../../tag';
import { BasicTable } from './basic-table';
import { PaginationItem } from './pagination-item';
import { Td } from './table-td';

const DIVIDEND_ROWS_PER_PAGE = 10;

const DIVIDEND_ROW_HEADERS: StringKey[] = [
  'PAYOUT_DATE',
  'STAKEHOLDER',
  'SHARE_CLASS',
  'PERIOD',
  'DIVIDEND_PER_SHARE',
  'NUMBER_OF_SHARES',
  'TOTAL_PAYOUT',
  'SIGNATURE_STATUS',
  'FILE',
  // Trailing actions column — the design keeps its header cell empty.
  'EMPTY',
];

const formatTimelineDate = (iso: null | string): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatPerShare = (value: null | number, currencySymbol: string): string =>
  value === null ? '-' : `${currencySymbol} ${formatDividendPerShare(value)}`;

const formatAmount = (value: null | number, currencySymbol: string): string =>
  value === null ? '-' : `${currencySymbol} ${formatDividendAmount(value)}`;

type Props = { companyId: string; payout: DividendTimelineEvent };

/**
 * Dividend payout card inside the events timeline. Read-only by design (QA 86cakz6u9 #14) — status
 * changes, edit and delete stay on the Dividends page. Rows load only once the card is expanded.
 */
export const DividendEventItem: FC<Props> = ({ companyId, payout }) => {
  const [isExpanded, setExpanded] = useState(false);
  const [isDetailsOpen, setDetailsOpen] = useState(false);
  const [detailsRow, setDetailsRow] = useState<DividendPayoutRow | null>(null);
  const [activeFilesModal, setActiveFilesModal] = useState<{
    createdAt: Date;
    filesLinks: string[];
    stakeholderName: string;
  } | null>(null);

  const company = selectedCompanyStorage(({ company }) => company);
  const currencySymbol = company?.currency?.symbol ?? '$';

  // Paged like every other Events table: one payout can carry dozens of stakeholder rows, and the
  // card listed all of them. The total comes with the payout, so no extra request counts them.
  const [currentPage, setCurrentPage] = usePagination(`events-dividend-${payout.id}`);
  const [itemsToFetch, setItemsToFetch] = useState(DIVIDEND_ROWS_PER_PAGE);

  const { records, isLoading } = DividendHooks.useDividendPayoutRows({
    companyId,
    payoutId: payout.id,
    enabled: isExpanded,
    skip: (currentPage - 1) * itemsToFetch,
    take: itemsToFetch,
  });

  const totalPages = Math.max(1, Math.ceil((payout.transactionsCount ?? 0) / itemsToFetch));

  // The details drawer needs the full payout (items + documents) — fetched only once it is opened.
  const { payout: payoutDetails } = DividendHooks.useDividendPayout({
    companyId,
    payoutId: payout.id,
    enabled: isDetailsOpen,
  });

  const { isOpen: isOpenFilesModal, handleCloseModal, handleOpenModal, toggler } = useModalState();

  const period = formatDividendPeriod(payout.frequency, payout.year, payout.periodIndex);
  const headerDate = payout.payoutDate ?? payout.approvalDate ?? payout.createdAt;

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(companyId, link);
  };

  const handleOpenFilesModalForRow = (
    filesLinks: string[],
    stakeholderName: string,
    date: Date,
  ) => {
    setActiveFilesModal({ createdAt: date, filesLinks, stakeholderName });
    handleOpenModal();
  };

  const handleCloseFilesModalWithReset = () => {
    setActiveFilesModal(null);
    handleCloseModal();
  };

  return (
    <div
      className={twMerge(
        'flex w-full flex-col gap-3 overflow-hidden rounded-md border border-gray-100 transition-colors duration-200',
        isExpanded ? 'bg-gray-50 px-[2px] pb-[2px]' : 'hover:bg-brand-25/30',
      )}
    >
      <AlertDialogWrapper control={{ onOpenChange: toggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={activeFilesModal?.createdAt}
          filesLinks={activeFilesModal?.filesLinks ?? []}
          name={activeFilesModal?.stakeholderName ?? ''}
          onClose={handleCloseFilesModalWithReset}
          onDownloadFile={handleDocumentClick}
          onSuccess={() => {
            void downloadCompanyStorageFilesSequentially(
              companyId,
              activeFilesModal?.filesLinks ?? [],
            );
            handleCloseFilesModalWithReset();
          }}
        />
      </AlertDialogWrapper>
      <DividendDetailsSheet
        companyId={companyId}
        currencySymbol={currencySymbol}
        isOpen={isDetailsOpen}
        onClose={() => setDetailsOpen(false)}
        payout={payoutDetails}
      />
      <DividendStakeholderDetailsSheet
        currencySymbol={currencySymbol}
        isOpen={!!detailsRow}
        onClose={() => setDetailsRow(null)}
        onDocumentClick={handleDocumentClick}
        period={period}
        row={detailsRow}
      />
      <div
        className="flex w-full cursor-pointer items-center justify-between gap-4 px-4 py-3"
        onClick={() => setExpanded((previous) => !previous)}
      >
        <div className="flex min-w-0 flex-col justify-center">
          <span className="truncate text-base font-semibold leading-8 text-gray-700">
            {payout.name} - {period}
          </span>
          <span className="text-label-sm font-medium leading-4 text-gray-500">
            {formatTimelineDate(headerDate)} - {payout.transactionsCount}{' '}
            <AppFormattedMessage id="TRANSACTIONS" />
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-4">
          <Tag
            borderColor="border-forest-200"
            title={`Total Payout: ${formatAmount(payout.totalAmount, currencySymbol)}`}
            variant={TagVariant.GREEN}
          />
          <Popover>
            <PopoverTrigger
              className="h-fit w-fit rounded data-[state=open]:shadow-md"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
                <ThreeDotsIcon />
              </div>
            </PopoverTrigger>
            <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
              <PopoverClose asChild onClick={(event) => event.stopPropagation()}>
                <div
                  className="flex w-full cursor-pointer items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setDetailsOpen(true)}
                >
                  <EyeIconSmall />
                  <span className="text-sm font-normal text-gray-700">
                    <AppFormattedMessage id="VIEW_DETAILS" />
                  </span>
                </div>
              </PopoverClose>
            </PopoverContent>
          </Popover>
          <ChevronDownIcon
            className={twMerge('size-5 text-gray-400 transition', isExpanded && 'rotate-180')}
          />
        </div>
      </div>
      {isExpanded && (
        <div className="w-full overflow-x-auto rounded-md bg-white px-2 pb-1 pt-3 shadow-sm">
          <BasicTable
            headers={DIVIDEND_ROW_HEADERS}
            isEmpty={!isLoading && records.length === 0}
            isLoading={isLoading}
            loadingRows={3}
          >
            {records.map((row) => (
              <tr key={`${row.stakeholderId}-${row.shareClassName ?? 'none'}`}>
                <Td value={formatTimelineDate(row.payoutDate)} />
                <Td value={row.stakeholderName} />
                <Td value={row.shareClassName ?? '—'} />
                <Td value={period} />
                <Td value={formatPerShare(row.dividendPerShare, currencySymbol)} />
                <Td value={row.shares === null ? '-' : row.shares.toLocaleString('en-US')} />
                <Td value={formatAmount(row.totalAmount, currencySymbol)} />
                <Td value="-" />
                <td className="px-4 py-6">
                  <EventTransactionFilesCell
                    date={new Date(row.createdAt)}
                    filesLinks={row.filesLinks ?? []}
                    onDocumentClick={handleDocumentClick}
                    onOpenFilesModal={handleOpenFilesModalForRow}
                    stakeholderName={row.stakeholderName}
                  />
                </td>
                <td className="w-12 px-4 py-6">
                  <Popover>
                    <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
                      <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
                        <ThreeDotsIcon />
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
                      <PopoverClose asChild>
                        <div
                          className="flex w-full cursor-pointer items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setDetailsRow(row)}
                        >
                          <EyeIconSmall />
                          <span className="text-sm font-normal text-gray-700">
                            <AppFormattedMessage id="VIEW_DETAILS" />
                          </span>
                        </div>
                      </PopoverClose>
                    </PopoverContent>
                  </Popover>
                </td>
              </tr>
            ))}
          </BasicTable>
          <PaginationItem
            currentPage={currentPage}
            handleChangeCurrentPage={setCurrentPage}
            handleFetchAmountChange={setItemsToFetch}
            itemsCount={records.length}
            itemsToFetch={itemsToFetch}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
};
